/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Transient UI Interaction Store (Zustand)
   Manages in-flight gestures, wire drafts, and modal visibility.
   Does NOT pollute command history.
   ═══════════════════════════════════════════════════════════════ */

import { create } from 'zustand';
import type { NodeId, PortId, PortType } from '../domain/types';

export interface WireDraft {
  sourceNodeId: NodeId;
  sourcePortId: PortId;
  sourceType: PortType;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

interface TransientStore {
  // In-flight wire connection
  wireDraft: WireDraft | null;
  startWireDraft: (draft: WireDraft) => void;
  updateWireDraft: (currentX: number, currentY: number) => void;
  clearWireDraft: () => void;

  // Selected preview node to monitor in the preview panel
  monitoredPreviewNodeId: NodeId | null;
  setMonitoredPreviewNodeId: (nodeId: NodeId | null) => void;

  // Modals & Palettes
  commandPaletteOpen: boolean;
  setCommandPaletteOpen: (open: boolean) => void;
  toggleCommandPalette: () => void;
}

export const useTransientStore = create<TransientStore>((set) => ({
  wireDraft: null,
  startWireDraft: (draft) => set({ wireDraft: draft }),
  updateWireDraft: (currentX, currentY) =>
    set((s) => ({
      wireDraft: s.wireDraft
        ? { ...s.wireDraft, currentX, currentY }
        : null,
    })),
  clearWireDraft: () => set({ wireDraft: null }),

  monitoredPreviewNodeId: null,
  setMonitoredPreviewNodeId: (nodeId) => set({ monitoredPreviewNodeId: nodeId }),

  commandPaletteOpen: false,
  setCommandPaletteOpen: (open) => set({ commandPaletteOpen: open }),
  toggleCommandPalette: () =>
    set((s) => ({ commandPaletteOpen: !s.commandPaletteOpen })),
}));
